import type { Editor, Range } from "@tiptap/core";
import type {
  SuggestionKeyDownProps,
  SuggestionProps,
} from "@tiptap/suggestion";
import type {
  SlashCommandItem,
  SlashCommandCategory,
} from "./useSlashCommandItems";
import { matchesSearchTokens } from "@/lib/search-utils";

const CATEGORY_LABELS: Record<SlashCommandCategory, string> = {
  project: "Projects",
  project_group: "Project Groups",
  meeting_note: "Meeting Notes",
  external_link: "Links",
};

const CATEGORY_ORDER: SlashCommandCategory[] = [
  "project",
  "project_group",
  "meeting_note",
  "external_link",
];
type SuggestionRenderProps = {
  lifecycle: SuggestionProps<SlashCommandItem, SlashCommandItem>;
  keyDown: SuggestionKeyDownProps;
};

// Inline SVG icons (16x16) from lucide
const CATEGORY_ICONS: Record<SlashCommandCategory, string> = {
  project: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 20a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8l-7 5V8l-7 5V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/></svg>`,
  project_group: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="14" y="3" rx="1"/><path d="M10 21V8a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1H3"/></svg>`,
  meeting_note: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>`,
  external_link: `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></svg>`,
};

function buildPopoverHTML(
  items: SlashCommandItem[],
  selectedIndex: number,
): string {
  if (items.length === 0) {
    return '<div class="px-2 py-1 text-muted-foreground text-sm">No results</div>';
  }

  let html = "";
  let currentCategory: string | null = null;
  let flatIndex = 0;

  for (const item of items) {
    if (item.category !== currentCategory) {
      currentCategory = item.category;
      const label = CATEGORY_LABELS[item.category] ?? item.category;
      if (flatIndex > 0) {
        html += '<div class="border-t border-border my-1"></div>';
      }
      html += `<div class="px-2 py-1 text-xs font-medium text-muted-foreground">${label}</div>`;
    }
    const isSelected = flatIndex === selectedIndex;
    const icon = CATEGORY_ICONS[item.category] ?? "";
    html += `<div class="px-2 py-1.5 rounded cursor-pointer text-sm flex items-center gap-2 ${isSelected ? "bg-accent" : "hover:bg-accent/50"}" data-index="${flatIndex}">${icon}<span class="truncate">${escapeHtml(item.label)}</span></div>`;
    flatIndex++;
  }

  return html;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function createSlashCommandSuggestion(
  itemsRef: React.MutableRefObject<SlashCommandItem[]>,
  onExternalLink: (range: Range) => void,
) {
  return {
    char: "/",
    items: ({ query }: { query: string }) => {
      const filtered: SlashCommandItem[] = [];

      // Group by category in order, filter by query
      for (const category of CATEGORY_ORDER) {
        const categoryItems = itemsRef.current
          .filter(
            (item) =>
              item.category === category &&
              matchesSearchTokens(item.label, query),
          )
          .slice(0, category === "external_link" ? 1 : 5);
        filtered.push(...categoryItems);
      }

      return filtered;
    },
    command: ({
      editor,
      range,
      props,
    }: {
      editor: Editor;
      range: Range;
      props: SlashCommandItem;
    }) => {
      if (props.id === "__external_link__") {
        // Delete the slash trigger text first, then open dialog
        editor.chain().focus().deleteRange(range).run();
        onExternalLink(range);
        return;
      }

      // Insert an inline link using the Link mark
      const nodeAfter = editor.view.state.selection.$to.nodeAfter;
      const overrideSpace = nodeAfter?.text?.startsWith(" ");
      if (overrideSpace) {
        range.to += 1;
      }

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: "text",
            text: props.label,
            marks: [{ type: "link", attrs: { href: props.route } }],
          },
          { type: "text", text: " " },
        ])
        .run();

      editor.view.dom.ownerDocument.defaultView
        ?.getSelection()
        ?.collapseToEnd();
    },
    render: () => {
      let component: HTMLDivElement | null = null;
      let selectedIndex = 0;
      let items: SlashCommandItem[] = [];
      let executeCommand: ((item: SlashCommandItem) => void) | null = null;

      const updateContent = () => {
        if (!component) return;
        component.innerHTML = buildPopoverHTML(items, selectedIndex);
        const selectedItem = component.querySelector(
          `[data-index="${selectedIndex}"]`,
        );
        selectedItem?.scrollIntoView({ block: "nearest" });
      };

      const selectItem = (index: number) => {
        if (!executeCommand || !items[index]) return;
        executeCommand(items[index]);
      };

      return {
        onStart: (props: SuggestionRenderProps["lifecycle"]) => {
          executeCommand = props.command;
          items = props.items;
          selectedIndex = 0;

          component = document.createElement("div");
          component.className =
            "bg-popover border border-border rounded-md shadow-lg p-1 z-50 min-w-[240px] max-h-[300px] overflow-y-auto";

          updateContent();

          component.addEventListener("mousedown", (e) => {
            e.preventDefault();
            const target = (e.target as HTMLElement).closest(
              "[data-index]",
            ) as HTMLElement | null;
            if (target?.dataset.index !== undefined) {
              selectItem(parseInt(target.dataset.index, 10));
            }
          });

          component.addEventListener("mousemove", (e) => {
            const target = (e.target as HTMLElement).closest(
              "[data-index]",
            ) as HTMLElement | null;
            if (!target?.dataset.index) return;
            const nextIndex = parseInt(target.dataset.index, 10);
            if (Number.isNaN(nextIndex) || nextIndex === selectedIndex) return;
            selectedIndex = nextIndex;
            updateContent();
          });

          const rect = props.clientRect?.();
          if (rect && component) {
            component.style.position = "fixed";
            component.style.left = `${rect.left}px`;
            component.style.top = `${rect.bottom + 4}px`;
            document.body.appendChild(component);
          }
        },
        onUpdate: (props: SuggestionRenderProps["lifecycle"]) => {
          executeCommand = props.command;
          items = props.items;
          selectedIndex = 0;
          updateContent();
        },
        onKeyDown: (props: SuggestionRenderProps["keyDown"]) => {
          if (items.length === 0) {
            return false;
          }
          if (props.event.key === "ArrowUp") {
            selectedIndex = (selectedIndex - 1 + items.length) % items.length;
            updateContent();
            return true;
          }
          if (props.event.key === "ArrowDown") {
            selectedIndex = (selectedIndex + 1) % items.length;
            updateContent();
            return true;
          }
          if (props.event.key === "Enter") {
            selectItem(selectedIndex);
            return true;
          }
          return false;
        },
        onExit: () => {
          component?.remove();
          component = null;
          executeCommand = null;
        },
      };
    },
  };
}
